import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import type { InitialUpload } from 'packages/pages/uploads/types';
import useElementCreator from 'packages/pages/editor/useElementCreator';
import useEditor from '~/hooks/useEditor';
import { getCreative } from 'packages/store/creatives/creatives.selectors';
import { saveCreativeTemplate } from 'packages/store/creativeEditor/creativeEditor.actions';

type LocationState = { initialUpload?: InitialUpload } | null;

/**
 * When navigated to the design editor with `state.initialUpload` set,
 * this component automatically creates an image element from that upload
 * on the first render (after the creative model is ready).
 *
 * It must be rendered inside EditorProvider so it can access the creative model
 * via useElementCreator.
 */
const InitialUploadPlacer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useThunkDispatch();
    const elementCreator = useElementCreator();
    const { creativeModel, exportIntegrations, exportConfigurationIds, approvalEnabled, reviewers } = useEditor();
    const creative = useSelector(getCreative)!;
    const placed = useRef(false);

    const state = location.state as LocationState;
    const initialUpload = state?.initialUpload;

    useEffect(() => {
        if (!initialUpload || placed.current) {
            return;
        }

        placed.current = true;

        const source = {
            id: initialUpload.id,
            type: 'ASSET_MANAGER' as const,
            src: initialUpload.thumbnails?.['1920x1920'] || initialUpload.fileUrl,
            name: initialUpload.name ?? initialUpload.fileName,
            width: initialUpload.width,
            height: initialUpload.height,
        };

        elementCreator.createImageElement(source);

        dispatch(
            saveCreativeTemplate(
                creative.id,
                creativeModel,
                exportIntegrations,
                exportConfigurationIds,
                () => {},
                approvalEnabled,
                reviewers,
                creative.type,
                true,
            ),
        );

        // Clear the navigation state to avoid re-placing the image if the user
        // navigates away and returns to this URL.
        navigate(location.pathname + location.search, { replace: true });
    }, []);

    return null;
};

export default InitialUploadPlacer;
